import type { AdminPermission } from '@/services/administrator-permissions/administrator-permissions.types'

export interface NavigationItem {
  title: string
  icon: string
  to?: string
  permission: AdminPermission | null
  hideInProduction?: boolean
  /** A page that ships later: the entry stays hidden until a route with this name exists. */
  routeName?: string
  /** A group entry's heading: drawn above the first entry of a run that shares it. */
  section?: string
  /** A tab on the page `to` opens, as its URL hash; the entry is current only while that tab is open. */
  hash?: string
  children?: NavigationItem[]
}

interface FilterContext {
  isProduction: boolean
  isSuperAdmin: boolean
  hasPermission: (permission: AdminPermission) => boolean
  /** Whether the router has a route by this name; entries naming one it lacks are hidden. */
  hasRoute?: (name: string) => boolean
}

/**
 * Filter the side-nav config down to what the current admin may see.
 *
 * `hideInProduction` outranks every permission, super admin included: those
 * entries point at routes carrying `meta.disallowOnProd`, so showing them in
 * production yields a nav item that only ever redirects to the dashboard.
 *
 * Mutates nothing — callers pass a config they own.
 */
export function filterNavigationItems(
  items: NavigationItem[],
  ctx: FilterContext
): NavigationItem[] {
  return items.reduce<NavigationItem[]>((visible, item) => {
    if (item.hideInProduction && ctx.isProduction) {
      return visible
    }

    if (item.routeName && ctx.hasRoute && !ctx.hasRoute(item.routeName)) {
      return visible
    }

    // Groups resolve before the super-admin check: a super admin still must not
    // see a hideInProduction child, and an early return would skip this branch.
    if (item.children) {
      const children = filterNavigationItems(item.children, ctx)
      if (children.length > 0) {
        visible.push({ ...item, children })
      }
      return visible
    }

    if (ctx.isSuperAdmin || !item.permission || ctx.hasPermission(item.permission)) {
      visible.push({ ...item })
    }
    return visible
  }, [])
}
